if (!customElements.get('external-3d-viewer')) {
  class External3dViewer extends HTMLElement {
    static loadApi() {
      if (window.Sketchfab) return Promise.resolve(window.Sketchfab);
      if (this.apiPromise) return this.apiPromise;

      this.apiPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://static.sketchfab.com/api/sketchfab-viewer-1.12.1.js';
        script.async = true;
        script.dataset.external3dApi = 'sketchfab';
        script.addEventListener('load', () => {
          if (window.Sketchfab) {
            resolve(window.Sketchfab);
          } else {
            reject(new Error('Sketchfab Viewer API loaded without a constructor'));
          }
        }, { once: true });
        script.addEventListener('error', () => {
          reject(new Error('Sketchfab Viewer API failed to load'));
        }, { once: true });
        document.head.appendChild(script);
      }).catch((error) => {
        this.apiPromise = undefined;
        throw error;
      });

      return this.apiPromise;
    }

    constructor() {
      super();

      this.openButton = this.querySelector('[data-external-3d-open]');
      this.closeButton = this.querySelector('[data-external-3d-close]');
      this.dialog = this.querySelector('[data-external-3d-dialog]');
      this.dialogPanel = this.querySelector('[data-external-3d-dialog-panel]');
      this.dialogFrame = this.dialogPanel?.querySelector('[data-sketchfab-model-id]');
      this.inlineMedia = this.querySelector('[data-external-3d-inline]');

      if (!this.openButton || !this.closeButton || !this.dialog || !this.dialogPanel || !this.dialogFrame || !this.inlineMedia) return;

      this.openHandler = this.open.bind(this);
      this.closeHandler = this.close.bind(this);
      this.cancelHandler = this.handleCancel.bind(this);
      this.keydownHandler = this.handleKeydown.bind(this);
      this.dialogCloseHandler = this.handleDialogClose.bind(this);
      this.inlineObserver = new MutationObserver(() => this.initializeInlineFrame());
      this.loadSequence = 0;
      this.openButtonViewportTop = 0;

      this.openButton.addEventListener('click', this.openHandler);
      this.closeButton.addEventListener('click', this.closeHandler);
      this.dialog.addEventListener('cancel', this.cancelHandler);
      this.dialog.addEventListener('close', this.dialogCloseHandler);
      this.inlineObserver.observe(this.inlineMedia, { childList: true });
    }

    disconnectedCallback() {
      if (!this.openButton || !this.dialog) return;

      this.openButton.removeEventListener('click', this.openHandler);
      this.closeButton.removeEventListener('click', this.closeHandler);
      this.dialog.removeEventListener('cancel', this.cancelHandler);
      this.dialog.removeEventListener('close', this.dialogCloseHandler);
      this.inlineObserver?.disconnect();
      document.removeEventListener('keydown', this.keydownHandler, true);
    }

    initializeInlineFrame() {
      const frame = this.inlineMedia.querySelector('[data-sketchfab-model-id]');
      if (!frame || frame.dataset.viewerInitializing === 'true') return;

      this.initializeFrame(frame, this.inlineMedia, () => {}, () => {
        this.inlineMedia.unloadContent();
        this.setViewerState(this.inlineMedia, 'error');
      });
    }

    async initializeFrame(frame, stateTarget, onReady, onError) {
      const loadId = String(++this.loadSequence);
      frame.dataset.viewerLoadId = loadId;
      frame.dataset.viewerInitializing = 'true';
      this.setViewerState(stateTarget, 'loading');
      let settled = false;
      const finish = (state, callback) => {
        if (settled) return;
        if (!frame.isConnected || frame.dataset.viewerLoadId !== loadId) {
          settled = true;
          window.clearTimeout(timeoutId);
          return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        frame.removeAttribute('data-viewer-load-id');
        frame.removeAttribute('data-viewer-initializing');
        frame.setAttribute('aria-hidden', state === 'ready' ? 'false' : 'true');
        this.setViewerState(stateTarget, state);
        callback();
      };
      const timeoutId = window.setTimeout(() => finish('error', onError), 20000);

      try {
        const Sketchfab = await External3dViewer.loadApi();
        if (!frame.isConnected || settled || frame.dataset.viewerLoadId !== loadId) return;
        const client = new Sketchfab('1.12.1', frame);
        client.init(frame.dataset.sketchfabModelId, {
          autostart: 1,
          camera: 0,
          dnt: 1,
          ui_controls: 0,
          ui_infos: 0,
          ui_stop: 0,
          ui_watermark_link: 0,
          success: (api) => {
            api.start();
            api.addEventListener('viewerready', () => finish('ready', onReady));
          },
          error: () => finish('error', onError)
        });
      } catch (error) {
        console.error('External 3D viewer failed to initialize.', error);
        finish('error', onError);
      }
    }

    setViewerState(target, state) {
      target.dataset.viewerState = state;
      if (target === this.inlineMedia) this.dataset.inlineViewerState = state;
      const status = target.querySelector('[data-external-3d-status]');
      const loading = target.querySelector('[data-external-3d-loading]');
      const error = target.querySelector('[data-external-3d-error]');
      if (!status || !loading || !error) return;

      status.hidden = state === 'ready';
      loading.hidden = state !== 'loading';
      error.hidden = state !== 'error';
    }

    open() {
      if (this.dialog.open) return;

      this.openButtonViewportTop = this.openButton.getBoundingClientRect().top;
      this.inlineMedia.unloadContent();
      this.hadOverflowHidden = document.body.classList.contains('overflow-hidden');
      document.body.classList.add('overflow-hidden');
      this.openButton.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this.keydownHandler, true);

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      this.initializeFrame(this.dialogFrame, this.dialogPanel, () => {
        this.closeButton.focus();
      }, () => {
        this.closeButton.focus();
      });
      this.closeButton.focus();
    }

    close() {
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.handleDialogClose();
      }
    }

    handleCancel(event) {
      event.preventDefault();
      this.close();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' || !this.dialog.open) return;

      event.preventDefault();
      event.stopPropagation();
      this.close();
    }

    handleDialogClose() {
      document.removeEventListener('keydown', this.keydownHandler, true);
      this.loadSequence += 1;
      this.dialogFrame.removeAttribute('src');
      this.dialogFrame.removeAttribute('data-viewer-load-id');
      this.dialogFrame.removeAttribute('data-viewer-initializing');
      this.dialogFrame.setAttribute('aria-hidden', 'true');
      this.dialogPanel.removeAttribute('data-viewer-state');
      this.openButton.setAttribute('aria-expanded', 'false');
      if (!this.hadOverflowHidden) document.body.classList.remove('overflow-hidden');

      const currentButtonTop = this.openButton.getBoundingClientRect().top;
      const restoredScrollPosition = window.scrollY + currentButtonTop - this.openButtonViewportTop;
      this.openButton.focus({ preventScroll: true });
      window.scrollTo({ top: Math.max(0, restoredScrollPosition), behavior: 'auto' });
    }
  }

  customElements.define('external-3d-viewer', External3dViewer);
}
